import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  GiftRecommendationResponse,
  Gift,
  UserEvent,
} from '../../../../../api/crawlFb';

interface GiftRecommendationModalProps {
  isOpen: boolean;
  onClose: () => void;
  giftData: GiftRecommendationResponse | null;
}

const GiftRecommendationModal: React.FC<GiftRecommendationModalProps> = ({
  isOpen,
  onClose,
  giftData,
}) => {
  if (!isOpen || !giftData) return null;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(price);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[95vw] w-full max-h-[95vh] p-0 border-0 flex flex-col"
        style={{ maxWidth: 'min(95vw, 1400px)' }}
        isHideCloseButton={true}
      >
        {/* Header */}
        <DialogHeader className="bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-700 p-4 sm:p-6 text-white rounded-t-lg flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg sm:text-xl font-bold text-white truncate">
                Gợi ý quà tặng từ AI
              </DialogTitle>
              <p className="text-sm text-white/80 mt-1 hidden sm:block">
                Dựa trên phân tích hoạt động và sự kiện
              </p>
            </div>
            <button
              onClick={onClose}
              className="cursor-pointer w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all duration-200 ml-4 flex-shrink-0"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
          {/* User Events Timeline */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">
                Hoạt động gần đây
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              {giftData.user_event.map((event: UserEvent, index: number) => (
                <div
                  key={event.event_key}
                  className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-4 border border-blue-200/50 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mr-2">
                        <span className="text-xs text-white font-bold">
                          {index + 1}
                        </span>
                      </div>
                      <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-1 rounded-full">
                        {event.start_date}
                      </span>
                    </div>
                  </div>

                  <h4 className="text-gray-800 font-bold text-sm mb-2">
                    {event.event}
                  </h4>

                  <div className="space-y-1 text-xs">
                    {event.estimated_date && (
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                        </svg>
                        <span>Dự kiến: {event.estimated_date}</span>
                      </div>
                    )}
                    {event.related_to && (
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                        <span>Liên quan: {event.related_to}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gift Recommendations */}
          <div className="space-y-6">
            {Object.entries(giftData.selected_gifts).map(
              ([eventKey, gifts]) => {
                const eventInfo = giftData.user_event.find(
                  (e) => e.event_key === eventKey
                );
                return (
                  <div
                    key={eventKey}
                    className="bg-gradient-to-br from-rose-50 to-pink-100 rounded-2xl p-6 border border-rose-200/50"
                  >
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl flex items-center justify-center mr-4">
                        <span className="text-lg">🎯</span>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-800">
                          Quà tặng cho: {eventInfo?.event || eventKey}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {eventInfo?.start_date}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {gifts.map((gift: Gift, giftIndex: number) => (
                        <div
                          key={`${eventKey}-${giftIndex}`}
                          className="group bg-white/90 backdrop-blur rounded-2xl p-4 sm:p-5 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/70 transform hover:-translate-y-1"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-200 rounded-2xl flex items-center justify-center mr-3">
                                <span className="text-xl">🎁</span>
                              </div>
                            </div>
                            <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-full px-3 py-1">
                              <span className="text-xs font-bold text-green-700">
                                {formatPrice(gift.price)}
                              </span>
                            </div>
                          </div>

                          <h5 className="font-bold text-gray-800 mb-2 group-hover:text-purple-700 transition-colors">
                            {gift.gift_name}
                          </h5>

                          <p className="text-sm text-gray-600 leading-relaxed">
                            {gift.reason}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start sm:items-center text-sm text-gray-600">
              <svg
                className="w-4 h-4 mr-2 mt-0.5 sm:mt-0 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
              </svg>
              <span className="leading-relaxed">
                Gợi ý được tạo bởi AI dựa trên phân tích hoạt động Facebook
              </span>
            </div>
            <button
              onClick={onClose}
              className="cursor-pointer bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-6 py-2 rounded-xl transition-all duration-200 font-medium w-full sm:w-auto"
            >
              Đóng
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GiftRecommendationModal;
